import type { TextProps } from '@mantine/core';
import { Text } from '@mantine/core';
import { useMemo } from 'react';

import type { CurrencyCode } from '../models';
import { DEFAULT_CULTURE } from '../models';

export type FormattedPriceProps = {
	value?: number | null;
	currency: CurrencyCode;
	hideCurrencyCode?: boolean;
	currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
} & Omit<TextProps, 'children'>;

export function FormattedPrice({
	value,
	currency,
	hideCurrencyCode = false,
	currencyDisplay = 'symbol',
	...textProps
}: FormattedPriceProps) {
	const formatter = useMemo(
		() =>
			new Intl.NumberFormat(DEFAULT_CULTURE, {
				style: 'currency',
				currency,
				currencyDisplay,
				currencySign: 'accounting',
			}),
		[currency, currencyDisplay],
	);

	const formattedPrice = useMemo(() => formatter.format(value ?? 0), [formatter, value]);

	if (value === undefined || value === null) {
		return null;
	}

	return (
		<Text c={value < 0 ? 'red.7' : undefined} {...textProps}>
			{formattedPrice}
			{!hideCurrencyCode && ` ${currency}`}
		</Text>
	);
}
